/*
    SnapshotManager 为用户的一致状态，建立可导出的本地持久化的快照序列，用于数据崩溃后的恢复
    @see: https://iwiki.woa.com/pages/viewpage.action?pageId=474632503
*/
import { PresentationEventTypes } from '@tencent/slide-sdk-model';
import { AutoSaveTask, AutoEvictTask } from './tasks';
import { SnapshotStorage } from './snapshot-storage';
import type { IEventManager } from '@tencent/slide-sdk-model/src/manager/event-manager';

export interface ISnapshotManagerConfig {
    globalPadId?: string;
    userId?: string;
}

export interface ISnapshotManager {
    ready: boolean;
    registered: boolean;
    getSnapshotStorage(): SnapshotStorage;
    register(config: ISnapshotManagerConfig): void;
    unregister(): void;
}

export class SnapshotManager implements ISnapshotManager {
    public ready = false;
    public registered = false;

    private storage: SnapshotStorage;

    private tasks: {
        save: AutoSaveTask;
        evict: AutoEvictTask;
    };

    constructor(
        private snapShotDataProvider: { getSnapshotData: () => unknown },
        private documentProvider: { onDocumentReady: (callback: () => void) => void },
        private configProvider: ISnapshotManagerConfig,
        private event: IEventManager,
        private logger: Pick<typeof console, 'debug' | 'error' | 'info' | 'log' | 'warn'>,
    ) {
        this.documentProvider.onDocumentReady(() => this.register());
    }

    public getSnapshotStorage(): SnapshotStorage {
        return this.storage;
    }

    public register(config: ISnapshotManagerConfig = this.configProvider) {
        if (this.registered) return;

        this.registered = true;

        this.initialize(config);
        this.start();
    }

    public unregister() {
        if (!this.registered) return;

        this.tasks.save.stop();
        this.tasks.evict.stop();
        this.unsubscribe();

        this.registered = false;
    }

    private initialize(config: ISnapshotManagerConfig) {
        if (!this.registered) {
            throw new Error('snapshot manager is not registered to slide app');
        }

        const { globalPadId, userId } = config;

        if (!globalPadId || !userId) return;

        this.storage = new SnapshotStorage(globalPadId, userId);

        this.tasks = {
            save: new AutoSaveTask(this.storage, this.logger, this.snapShotDataProvider.getSnapshotData),
            evict: new AutoEvictTask(this.storage, this.logger),
        };

        this.ready = true;
    }

    private start() {
        if (!this.ready) return;

        this.tasks.save.start();
        this.tasks.evict.start();
        this.subscribe();
    }

    private subscribe() {
        this.event.on(PresentationEventTypes.CommitCommand, this.handleCommitMutations);
    }

    private unsubscribe() {
        this.event.off(PresentationEventTypes.CommitCommand, this.handleCommitMutations);
    }

    private handleCommitMutations = () => {
        // 与 CommitCommand 事件 1:1
        this.tasks.save.update((state) => state.increment(1));
    };
}
